import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const STATUS = ["Entwurf", "Zur Freigabe", "Veröffentlicht", "Archiviert"];
const empty = {
  title: "", location: "", region: "", object_type: "", service_type: "",
  situation: "", scope: "", result: "", before_image: "", after_image: "",
  status: "Entwurf", show_home: false, show_reference: true, order: 0,
  seo_title: "", meta_description: "",
  city_slug: "", city_name: "", show_city_relation: false,
};

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function Projects() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} project
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/projects");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/admin/cities").then((r) => setCities(r.data)).catch(() => setCities([]));
  }, []);

  const onImage = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error("Bild zu groß (max. 6 MB).");
    const url = await fileToDataUrl(file);
    setEditing((p) => ({ ...p, [key]: url }));
  };

  const save = async () => {
    if (!editing.title?.trim()) return toast.error("Bitte einen Titel angeben.");
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      if (editing.id) await api.patch(`/admin/projects/${editing.id}`, payload);
      else await api.post("/admin/projects", payload);
      toast.success("Projekt gespeichert.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Projekt löschen?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Gelöscht.");
    load();
  };

  const inputCls = "mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow";

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-anthracite">Vorher-Nachher-Projekte</h1>
          <p className="mt-1 text-mid-gray">Werden erst nach Freigabe veröffentlicht.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark" data-testid="project-new">
          <Plus className="h-4 w-4" /> Neues Projekt
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
      ) : items.length === 0 ? (
        <p className="mt-10 rounded-md border border-dashed border-mid-gray/30 bg-white p-12 text-center text-mid-gray" data-testid="projects-empty">Noch keine Projekte angelegt.</p>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => (
            <div key={p.id} className="rounded-md border border-light-gray bg-white p-4" data-testid={`project-${p.id}`}>
              <div className="flex gap-2">
                {p.before_image && <img src={p.before_image} alt="Vorher" className="h-20 w-1/2 rounded-sm object-cover" />}
                {p.after_image && <img src={p.after_image} alt="Nachher" className="h-20 w-1/2 rounded-sm object-cover" />}
              </div>
              <h3 className="mt-3 font-heading font-bold text-anthracite">{p.title}</h3>
              <p className="text-sm text-mid-gray">{p.location} · {p.region}</p>
              <div className="mt-2 flex items-center justify-between">
                <span className="rounded-sm bg-light-gray px-2 py-1 text-xs font-semibold text-mid-gray">{p.status}</span>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(p)} className="text-sm font-bold text-anthracite hover:text-yellow-dark">Bearbeiten</button>
                  <button onClick={() => del(p.id)} className="text-red-500" data-testid={`project-del-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/60 p-4" data-testid="project-editor">
          <div className="my-8 w-full max-w-2xl rounded-md bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-anthracite">{editing.id ? "Projekt bearbeiten" : "Neues Projekt"}</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-mid-gray" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Projekttitel *<input value={editing.title} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} className={inputCls} data-testid="project-title" /></label>
              <label className="text-sm font-semibold text-anthracite">Einsatzort<input value={editing.location} onChange={(e) => setEditing((p) => ({ ...p, location: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite">Region<input value={editing.region} onChange={(e) => setEditing((p) => ({ ...p, region: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite">Objektart<input value={editing.object_type} onChange={(e) => setEditing((p) => ({ ...p, object_type: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite">Leistungsart<input value={editing.service_type} onChange={(e) => setEditing((p) => ({ ...p, service_type: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Ausgangssituation<textarea value={editing.situation} onChange={(e) => setEditing((p) => ({ ...p, situation: e.target.value }))} className={inputCls} rows={2} /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Ergebnis<textarea value={editing.result} onChange={(e) => setEditing((p) => ({ ...p, result: e.target.value }))} className={inputCls} rows={2} /></label>
              <label className="text-sm font-semibold text-anthracite">Vorher-Bild<input type="file" accept="image/*" onChange={(e) => onImage(e, "before_image")} className="mt-1 w-full text-sm" data-testid="project-before" />{editing.before_image && <img src={editing.before_image} alt="" className="mt-2 h-24 rounded-sm object-cover" />}</label>
              <label className="text-sm font-semibold text-anthracite">Nachher-Bild<input type="file" accept="image/*" onChange={(e) => onImage(e, "after_image")} className="mt-1 w-full text-sm" data-testid="project-after" />{editing.after_image && <img src={editing.after_image} alt="" className="mt-2 h-24 rounded-sm object-cover" />}</label>
              <label className="text-sm font-semibold text-anthracite">Status<select value={editing.status} onChange={(e) => setEditing((p) => ({ ...p, status: e.target.value }))} className={inputCls} data-testid="project-status">{STATUS.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label className="text-sm font-semibold text-anthracite">Reihenfolge<input type="number" value={editing.order} onChange={(e) => setEditing((p) => ({ ...p, order: Number(e.target.value) }))} className={inputCls} /></label>
              <label className="flex items-center gap-2 text-sm text-anthracite"><input type="checkbox" checked={editing.show_home} onChange={(e) => setEditing((p) => ({ ...p, show_home: e.target.checked }))} className="accent-yellow" /> Auf Startseite</label>
              <label className="flex items-center gap-2 text-sm text-anthracite"><input type="checkbox" checked={editing.show_reference} onChange={(e) => setEditing((p) => ({ ...p, show_reference: e.target.checked }))} className="accent-yellow" /> Auf Referenzseite</label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Zugeordneter Ort (Lokale SEO)
                <select
                  value={editing.city_slug || ""}
                  onChange={(e) => {
                    const c = cities.find((x) => x.slug === e.target.value);
                    setEditing((p) => ({ ...p, city_slug: e.target.value, city_name: c ? c.name : "" }));
                  }}
                  className={inputCls}
                  data-testid="project-city"
                >
                  <option value="">— kein Ort —</option>
                  {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.status})</option>)}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-anthracite sm:col-span-2"><input type="checkbox" checked={!!editing.show_city_relation} onChange={(e) => setEditing((p) => ({ ...p, show_city_relation: e.target.checked }))} className="accent-yellow" data-testid="project-show-city" /> Ortsbezug öffentlich anzeigen (Projekt auf der zugeordneten Ortsseite zeigen)</label>
            </div>
            <button onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-sm bg-yellow px-5 py-3 font-heading font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60" data-testid="project-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
